import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import {
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { usePermissions } from '@/shared/permissions';
import { ErrorState } from '@/shared/ui';

import { useAccessReview } from '../../hooks';
import { buildDepartmentsCsv, buildPeopleCsv, downloadCsv } from '../../lib/accessCsv';
import { isPersonFlagged } from '../../lib/reviewFlags';
import { useDepartmentReview } from '../../useDepartmentReview';
import { AccessHistoryDrawer } from '../components/AccessHistoryDrawer';
import { AccessReviewSummary } from '../components/AccessReviewSummary';
import { DepartmentAttestation } from '../components/DepartmentAttestation';
import { EmployeeAccessPanel } from '../components/EmployeeAccessPanel';

/** The two units a review is conducted in. Only ever one of them is on screen. */
type ReviewSegment = 'departments' | 'people';

/** The permission that opens the review — the same one the route guard and the BFF enforce. */
const REVIEW_PERMISSION = 'employees.manage';

/**
 * The periodic access review (nfr-merchant-portal-002, plan-auth-001 Phase 4.5).
 *
 * <p>A merchant's security officer sits down with this once a quarter and asks two questions, in this
 * order:
 *
 * <ol>
 *   <li><b>Should this department still confer this?</b> — {@link DepartmentAttestation}. Rights are
 *   attached to departments and people inherit them, so this is where the answer can actually be acted
 *   on: confirming or revoking a department is one decision that reaches everyone in it.</li>
 *   <li><b>Should this person still have this?</b> — {@link EmployeeAccessPanel}, which shows what those
 *   department decisions add up to per employee.</li>
 * </ol>
 *
 * <h3>Why this is a segmented screen and not two stacked tables</h3>
 *
 * <p>It used to be both listings, one under the other. That reads fine with four departments and six
 * employees and collapses the moment a real company opens it: two long tables compete for one viewport,
 * neither can be focused on, and the reviewer scrolls past the half they are not working on to reach the
 * half they are. Worse, neither answers the two questions the task actually starts and ends with — *what
 * looks wrong?* and *what is left?*
 *
 * <p>So the screen is built as the task: a **summary of what needs attention first**, then **one listing
 * at a time** behind a segmented control. The summary tiles are not decoration — each one jumps into the
 * listing it counts with the matching filter applied, so the number a reviewer reads is the work queue
 * they are about to open. Master-detail is used inside the employee listing rather than across the whole
 * screen, because the thing that does not fit a row is one person's per-domain access, not the choice of
 * which unit to review.
 *
 * <p>The filters, search, export, history and the department revoke are the parts that already worked and
 * are carried over unchanged in behaviour; what changed is that only one of them is ever competing for
 * the reviewer's attention.
 */
export function AccessReviewPage() {
  const { t } = useTranslation('access');
  const { hasPermission } = usePermissions();
  const canView = hasPermission(REVIEW_PERMISSION);

  const [segment, setSegment] = useState<ReviewSegment>('departments');
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [peopleSearch, setPeopleSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [historyOpen, setHistoryOpen] = useState(false);

  // `enabled` rather than "render nothing": a reviewer without the permission must issue no request at
  // all. The BFF would refuse it anyway, so firing it only buys a 403 in the audit log.
  const departments = useDepartmentReview({ enabled: canView });
  const review = useAccessReview(
    { page, size, search: peopleSearch.trim() || undefined },
    { enabled: canView },
  );

  // `?? []` allocates a new array on every render, which would make the memo below re-run every time —
  // memoise the fallback with it so the dependency is actually stable.
  const rows = useMemo(() => review.data?.items ?? [], [review.data]);
  const shownPeople = useMemo(
    () => (flaggedOnly ? rows.filter(isPersonFlagged) : rows),
    [rows, flaggedOnly],
  );
  const flaggedPeople = useMemo(() => rows.filter(isPersonFlagged).length, [rows]);

  const shownDepartments = useMemo(() => {
    const query = departmentSearch.trim().toLowerCase();
    return departments.rows.filter((row) => {
      if (flaggedOnly && !row.needsAttention) return false;
      if (!query) return true;
      return (
        row.group.name.toLowerCase().includes(query) ||
        (row.group.path ?? '').toLowerCase().includes(query)
      );
    });
  }, [departments.rows, departmentSearch, flaggedOnly]);

  if (!canView) {
    return (
      <ErrorState title={t('review.restricted.title')} message={t('review.restricted.message')} />
    );
  }

  const onDepartments = segment === 'departments';
  const exportable = onDepartments ? shownDepartments.length > 0 : shownPeople.length > 0;

  const openSegment = (next: ReviewSegment, flagged: boolean) => {
    setSegment(next);
    setFlaggedOnly(flagged);
  };

  /**
   * Exports what is on screen, in the unit that is on screen.
   *
   * <p>Both exports carry the *filtered* rows, not the whole tenant: the reviewer is attaching evidence
   * for the decision they just made, and an export that quietly contained rows they never looked at would
   * misrepresent the review. The department export exists because it is the one the inline note promises
   * — confirming a department is recorded nowhere, so this is the only evidence an unchanged department
   * can have.
   */
  const exportCurrent = () => {
    if (onDepartments) {
      downloadCsv(
        buildDepartmentsCsv(
          shownDepartments.map((row) => ({
            name: row.group.name,
            path: row.group.path,
            memberCount: row.group.memberCount,
            rights: row.rights,
            flags: row.flags,
          })),
        ),
        t('review.departments.exportFilename'),
      );
      return;
    }
    downloadCsv(
      buildPeopleCsv(
        shownPeople,
        departments.domains.map((domain) => domain.code),
      ),
      t('review.exportFilename'),
    );
  };

  return (
    <Stack spacing={3} sx={{ minWidth: 0 }}>
      <AccessReviewSummary
        grantingDepartments={departments.grantingCount}
        totalDepartments={departments.rows.length}
        flaggedDepartments={departments.flaggedCount}
        flaggedPeople={flaggedPeople}
        peopleOnPage={rows.length}
        loading={departments.isLoading || review.isLoading}
        onOpenDepartments={(flagged) => openSegment('departments', flagged)}
        onOpenPeople={(flagged) => openSegment('people', flagged)}
      />

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ md: 'center' }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={segment}
              onChange={(_, next: ReviewSegment | null) => next && setSegment(next)}
              aria-label={t('review.segments.aria')}
            >
              <ToggleButton
                value="departments"
                sx={{ textTransform: 'none', gap: 0.75 }}
                // The count rides in the accessible name too: a bare "Departments 1" leaves someone
                // who cannot see the warning-coloured chip to guess what the 1 counts.
                aria-label={segmentLabel(
                  t('review.segments.departments'),
                  departments.flaggedCount,
                  t,
                )}
              >
                {t('review.segments.departments')}
                {departments.flaggedCount > 0 && (
                  <Chip size="small" color="warning" label={departments.flaggedCount} />
                )}
              </ToggleButton>
              <ToggleButton
                value="people"
                sx={{ textTransform: 'none', gap: 0.75 }}
                aria-label={segmentLabel(t('review.segments.people'), flaggedPeople, t)}
              >
                {t('review.segments.people')}
                {flaggedPeople > 0 && <Chip size="small" color="warning" label={flaggedPeople} />}
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1}>
              <Button startIcon={<HistoryRoundedIcon />} onClick={() => setHistoryOpen(true)}>
                {t('review.history')}
              </Button>
              <Button
                startIcon={<DownloadRoundedIcon />}
                disabled={!exportable}
                onClick={exportCurrent}
              >
                {t('review.export')}
              </Button>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ sm: 'center' }}
          >
            <TextField
              size="small"
              label={onDepartments ? t('review.departments.search') : t('review.search')}
              value={onDepartments ? departmentSearch : peopleSearch}
              onChange={(event) => {
                if (onDepartments) {
                  setDepartmentSearch(event.target.value);
                  return;
                }
                setPeopleSearch(event.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 260 }}
            />
            <Tooltip
              title={onDepartments ? t('review.departments.flaggedHint') : t('review.flaggedHint')}
            >
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Switch
                  size="small"
                  checked={flaggedOnly}
                  onChange={(event) => setFlaggedOnly(event.target.checked)}
                  slotProps={{ input: { 'aria-label': t('review.flaggedOnly') } }}
                />
                <Typography variant="body2">{t('review.flaggedOnly')}</Typography>
              </Stack>
            </Tooltip>
          </Stack>
        </Stack>

        {onDepartments ? (
          <DepartmentAttestation
            onOpenHistory={() => setHistoryOpen(true)}
            search={departmentSearch}
            flaggedOnly={flaggedOnly}
          />
        ) : (
          <EmployeeAccessPanel
            rows={shownPeople}
            domains={departments.domains}
            total={review.data?.total ?? -1}
            page={page}
            size={size}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
            isLoading={review.isLoading}
            isError={review.isError}
            errorMessage={review.error?.message ?? ''}
            onRetry={() => void review.refetch()}
            flaggedOnly={flaggedOnly}
            searching={peopleSearch.trim().length > 0}
          />
        )}
      </Stack>

      <AccessHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </Stack>
  );
}

/**
 * A segment's accessible name: the unit, plus what it has waiting, when there is anything.
 *
 * <p>Keeps the visible label as the first words of the name (WCAG 2.5.3) so a voice-control user can
 * still say "Departments".
 */
function segmentLabel(
  name: string,
  flagged: number,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  return flagged > 0 ? t('review.segments.flagged', { name, count: flagged }) : name;
}
